import React, { useRef, useLayoutEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useZmanimData } from '../useZmanimData';

// וודא שהנתיבים לקבצים אלו נכונים בפרויקט שלך
import './Normal.scss';

// --- הגדרות עיצוב וקבועים ---

// הקטנו את הערך מ-1.2 ל-0.75 כדי שהציר לא יהיה ארוך מדי ולא יכריח את התוכן להתכווץ יותר מדי
const MIN_PIXELS_PER_MINUTE = 0.3;

const COLORS = {
  navy: "#1e3a5f",
  navyLight: "#2d5a8a",
  navyDark: "#152a45",
  gold: "#d4a84b",
  goldDark: "#b8923e",
  teal: "#4a9d8e",
  emerald: "#3d9970",
  slate: "#64748b",
  border: "#e5ddd3",
  cream: "#fdfbf7",
  creamLight: "#faf8f4",
  white: "#ffffff",
  emergency: "#ea4335",
  emergencyLight: "#fef2f2",
};

// --- קומפוננטות עזר (Sub-Components) ---

const FlexibleSpacer = ({ minutes }) => {
  const minH = Math.max(0, minutes * MIN_PIXELS_PER_MINUTE);
  return (
    <div
      style={{
        flexGrow: minutes,
        minHeight: `${minH}px`,
        flexShrink: 0,
        flexBasis: '0px'
      }}
    />
  );
};

// --- פריט רגיל (Compact) ---
const SimpleItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;

  return (
    <div className="relative flex gap-3 items-center flex-shrink-0" style={{ direction: 'rtl', opacity: isPast ? 0.8 : 1, padding: '8px 0' }}>
      {/* Icon Wrapper */}
      <div className="flex flex-col items-center relative w-12 shrink-0">
        <div
          className="rounded-full p-2 flex items-center justify-center z-10"
          style={{
            backgroundColor: COLORS.white,
            boxShadow: isCurrentTime ? `0 0 0 3px ${COLORS.emergency}20` : `0 0 0 3px ${COLORS.white}`
          }}
        >
          <IconComponent
            className="w-10 h-10"
            style={{ color: isCurrentTime ? COLORS.emergency : (isPast ? COLORS.slate : COLORS.gold) }}
          />
        </div>
      </div>

      {/* Text Content */}
      <div className="flex-1 flex justify-between items-center gap-2 px-2 py-0 ml-12 mr-12" style={{ minWidth: 0 }}>
        <div className="flex flex-col" style={{ minWidth: 0, overflow: 'hidden' }}>
          <span
            className="font-semibold leading-tight"
            style={{
              color: isCurrentTime ? COLORS.emergency : (isPast ? COLORS.slate : COLORS.navy),
              fontSize: '1em',
              lineHeight: '1.1',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {item.title}
          </span>
          {item.subtitle && (
            <span className="leading-tight" style={{
              color: COLORS.slate,
              fontSize: '0.8em',
              lineHeight: '1.1',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {item.subtitle}
            </span>
          )}
        </div>
        <span
          className="font-bold leading-tight"
          style={{
            color: isCurrentTime ? COLORS.emergency : COLORS.slate,
            fontSize: '1em',
            lineHeight: '1.1',
            flexShrink: 0
          }}
        >
          {item.time}
        </span>
      </div>
    </div>
  );
};

// --- פריט ענייני היום (Annotation) ---
const AnnotationItem = ({ item, isPast }) => {
  const pills = item.items || [{ value: item.title }];
  return (
    <div className="relative flex gap-3 items-start flex-shrink-0" style={{ direction: 'rtl', opacity: isPast ? 0.8 : 1 }}>
      <div className="flex flex-col items-center relative w-12 shrink-0" style={{ paddingTop: '6px' }}>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%',
          backgroundColor: COLORS.gold,
          border: `2px solid ${COLORS.white}`,
          boxShadow: `0 0 0 2px ${COLORS.gold}30`,
          zIndex: 10,
        }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '2px 0' }}>
        {pills.map((pill, idx) => (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center',
            backgroundColor: `${COLORS.gold}08`,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '1px 8px',
            fontSize: '0.72em', fontWeight: 500, color: COLORS.navy,
            lineHeight: 1.3, maxWidth: '100%',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {pill.value || pill.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// --- פריט מודגש (Compact) ---
const HighlightedItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  const accentColor = item.accentColor || COLORS.navy;

  return (
    <div className="relative flex gap-3 flex-shrink-0" style={{ direction: 'rtl', opacity: isPast ? 0.8 : 1, padding: '8px 0' }}>
      {/* Icon Wrapper */}
      <div className="flex flex-col items-center relative w-12 shrink-0 pt-3">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center z-10"
          style={{
            backgroundColor: `${accentColor}15`,
            color: accentColor,
            border: isCurrentTime ? `2px solid ${COLORS.emergency}` : `2px solid ${COLORS.border}`,
            boxShadow: isCurrentTime ? `0 0 0 3px ${COLORS.emergency}20` : `0 0 0 3px ${COLORS.white}`
          }}
        >
          <IconComponent size={40}  />
        </div>
      </div>

      <div className="flex-1">
        <div
          className="relative overflow-hidden mr-10 ml-10"
          style={{
            backgroundColor: `${accentColor}10`,
            border: isCurrentTime ? `2px solid ${COLORS.emergency}` : `2px solid ${COLORS.border}`,
            boxShadow: isCurrentTime ? `0 4px 10px ${COLORS.emergency}15` : '0 1px 3px rgba(30, 58, 95, 0.06)',
            borderRadius: '36px',
            padding: '12px 24px'
          }}
        >
          <div
            className="absolute top-0 right-0 w-1.5 h-full"
            style={{ backgroundColor: isCurrentTime ? COLORS.emergency : accentColor }}
          />

          <div className="flex justify-between items-start gap-2 mb-1" style={{ minWidth: 0 }}>
            <span className="font-bold leading-tight" style={{
              color: COLORS.navy,
              fontSize: '1.1em',
              lineHeight: '1.1',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0
            }}>
              {item.title}
            </span>
            <span
              className="font-bold px-6 py-1 rounded-3xl flex items-center"
              style={{
                backgroundColor: `${accentColor}10`,
                color: COLORS.navy,
                border: `1px solid ${accentColor}30`,
                fontSize: '1em',
                lineHeight: '1.1',
                flexShrink: 0
              }}
            >
              {item.time}
            </span>
          </div>
          {item.subtitle && (
            <span className="leading-tight block" style={{
              color: COLORS.slate,
              fontSize: '0.8em',
              lineHeight: '1.1',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {item.subtitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// --- אינדיקטור לזמן הנוכחי (Compact) ---
const NowIndicator = ({ currentTimeStr }) => (
  <div className="relative flex items-center flex-shrink-0" style={{ direction: 'rtl' }}>
    {/* קו אדום דק עד הציר */}
    <div
      className="absolute h-0"
      style={{
        borderTop: `3px solid ${COLORS.gold}`,
        zIndex: 0,
        right: '25px', // מיקום הציר
        left: '0',
        top: '50%',
        transform: 'translateY(-1px)'
      }}
    />

    <div className="flex items-center relative w-12 shrink-0 z-20">
      {/* הסרנו את האייקון */}
    </div>

    <div className="relative z-10 flex-1 flex justify-center">
      <span
        className="font-bold px-6 py-2 rounded-full"
        style={{
          backgroundColor: COLORS.gold,
          color: COLORS.navy,
          border: `3px solid ${COLORS.navy}40`,
          boxShadow: `0 8px 20px ${COLORS.navy}30`,
          whiteSpace: 'nowrap',
          display: 'inline-block',
          minWidth: '180px',
          textAlign: 'center',
          fontSize: '1.8em',
          lineHeight: '1.2',
        }}
      >
        {currentTimeStr}
      </span>
    </div>
  </div>
);

// --- פריט סיידבר (Ultra Compact) ---
const PastItem = ({ item, isPast }) => {
  const IconComponent = item.icon;
  const accentColor = item.accentColor || COLORS.navy;

  return (
    <div
      className="flex items-center gap-2 p-2 transition-all duration-300"
      style={{
        backgroundColor: `${accentColor}08`,
        border: `1px solid ${COLORS.border}`,
        opacity: isPast ? 0.5 : 0.8,
        borderRadius: '12px'
      }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
      >
        <IconComponent className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div
          className="text-xs font-bold truncate"
          style={{ color: COLORS.navy, lineHeight: 1.2 }}
          title={item.title}
        >
          {item.title}
        </div>
        <div className="text-[10px] font-mono" style={{ color: isPast ? COLORS.slate : COLORS.teal }}>
          {item.time}
        </div>
      </div>
    </div>
  );
};


// --- הקומפוננטה הראשית ---

const Normal = ({ zmanimMap = {}, customScheduleItems = [], title = 'סדר היום', showTitle = true, hebrewDate = null, inyaneiItems = null, inyaneiDisplayMode = null }) => {
  const [layoutState, setLayoutState] = useState({ scale: 1, heightPercent: 100 });

  const containerRef = useRef(null);
  const innerListRef = useRef(null);

  const {
    processedItems,
    nowItemIndex,
    currentTimeStr,
    currentItemMatch,
    currentTotalMinutes,
    dayWithHebrewDate,
  } = useZmanimData({
    zmanimMap,
    customScheduleItems,
    hebrewDate,
    prayerColor: COLORS.goldDark,
    lessonColor: COLORS.navy,
  });

  // --- Layout Effect (עם התיקון לרוחב) ---
  useLayoutEffect(() => {
    const calculateLayout = () => {
      if (!containerRef.current || !innerListRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      const el = innerListRef.current;

      el.style.width = '100%';
      el.style.height = 'auto';

      const contentHeight = el.scrollHeight;

      let newScale = 1;
      let newHeightPercent = 100;

      if (contentHeight > containerHeight) {
        newScale = containerHeight / contentHeight;
        
        // בדיקה שנייה: האם הרחבת הרוחב (פיצוי על הסקייל) הקטינה את הגובה?
        // זה קורה כשטקסט שנשבר לשורות חוזר לשורה אחת בגלל שהרוחב גדל
        el.style.width = `${100 / newScale}%`;
        const newContentHeight = el.scrollHeight;

        if (newContentHeight < contentHeight) {
          // חישוב סקייל מדויק יותר לפי הגובה החדש
          newScale = Math.min(1, containerHeight / newContentHeight);
        }

        newHeightPercent = (100 / newScale);
      }

      setLayoutState(prev => {
        const isScaleChanged = Math.abs(prev.scale - newScale) > 0.001;
        const isHeightChanged = Math.abs(prev.heightPercent - newHeightPercent) > 0.1;
        if (!isScaleChanged && !isHeightChanged) {
          el.style.width = `${100 / prev.scale}%`;
          return prev;
        }
        return { scale: newScale, heightPercent: newHeightPercent };
      });
      el.style.width = `${100 / newScale}%`;
    };

    calculateLayout();
    const observer = new ResizeObserver(() => window.requestAnimationFrame(calculateLayout));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [processedItems]);

  const showInyaneiSidebar = (inyaneiDisplayMode === 'sidebar' || inyaneiDisplayMode === 'combined') && inyaneiItems?.length > 0;
  const showInyaneiSection = inyaneiDisplayMode === 'section' && inyaneiItems?.length > 0;
  const showSidebarColumn = showInyaneiSidebar;

  const gridRows = [];
  if (showTitle) gridRows.push('auto');
  if (showInyaneiSection) gridRows.push('auto');
  gridRows.push('1fr');

  return (
    <div
      className="template-timeline"
      style={{
        direction: 'rtl',
        width: '100%',
        height: '100%',
        backgroundColor: COLORS.creamLight,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: gridRows.join(' '),
        gridTemplateColumns: showSidebarColumn ? '18% 1fr' : '1fr',
        fontSize: 'inherit'
      }}
    >

      {/* 1. Header */}
      {showTitle && (
        <div
          style={{
            gridColumn: '1 / -1',
            backgroundColor: 'rgb(139 109 8 / 70%)',
            borderBottom: `2px solid ${COLORS.border}`,
            padding: '0.75rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 20,
            height: 'min-content'
          }}
        >
          <div className="flex items-center gap-2 font-bold" style={{ color: COLORS.creamLight, fontSize: '20px' }}>
            <Clock size={20} />
            <span>{title}</span>
          </div>
          <span
            className="font-semibold px-6 py-2 rounded-full"
            style={{
              backgroundColor: COLORS.creamLight,
              border: '1px solid ' + COLORS.gold,
              minWidth: '200px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              fontSize: '20px',
              color: '#8b6d08',
              borderRadius: '20px',
              padding: '4px'
            }}
          >
            {dayWithHebrewDate}
          </span>
        </div>
      )}

      {/* 2. Inyanei Section (below header, full width) */}
      {showInyaneiSection && (
        <div style={{
          gridColumn: '1 / -1',
          borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: `${COLORS.gold}08`,
          padding: '6px 1.5rem',
          display: 'flex', flexWrap: 'wrap', gap: '5px',
          alignItems: 'center', direction: 'rtl',
        }}>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '14px', padding: '2px 10px',
                fontSize: '0.72rem', fontWeight: 600, color: COLORS.navy,
                whiteSpace: 'nowrap',
              }}>
                <Icon size={12} style={{ color: COLORS.gold, flexShrink: 0 }} />
                {item.value || item.label}
              </span>
            );
          })}
        </div>
      )}

      {/* 3. Inyanei Sidebar */}
      {showSidebarColumn && (
        <div style={{
          overflowY: 'auto', padding: '0.75rem',
          backgroundColor: `${COLORS.gold}06`,
          borderRight: `2px solid ${COLORS.border}`,
          direction: 'rtl',
          display: 'flex', flexDirection: 'column', gap: '5px',
        }}>
          <div style={{ fontWeight: 700, color: '#8b6d08', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
            ענייני היום
          </div>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            const isHigh = item.priority === 'high';
            return (
              <div key={`iny-${idx}`} style={{
                display: 'flex', alignItems: 'flex-start', gap: '5px',
                backgroundColor: COLORS.white,
                border: `1px solid ${isHigh ? `${COLORS.gold}80` : COLORS.border}`,
                borderRadius: '8px', padding: '4px 8px',
              }}>
                <Icon size={13} style={{ color: isHigh ? '#b45309' : COLORS.gold, flexShrink: 0, marginTop: '2px' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: COLORS.navy, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {item.value || item.label}
                  </div>
                  {item.detail && (
                    <div style={{ fontSize: '0.6rem', color: COLORS.slate, lineHeight: 1.2, marginTop: '1px' }}>
                      {item.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 3. Main Timeline Area */}
      <div
        ref={containerRef}
        style={{
          backgroundColor: COLORS.white,
          position: 'relative',
          overflow: 'hidden',
          height: '100%'
        }}
      >
        <div
          ref={innerListRef}
          style={{
            width: `${100 / layoutState.scale}%`,
            height: `${layoutState.heightPercent}%`,
            transform: `scale(${layoutState.scale})`,
            transformOrigin: 'top right',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            padding: '1.5rem',
            boxSizing: 'border-box'
          }}
        >
          {/* קו רקע - מותאם למרכז של אייקון בגודל 48px/12 */}
          {/* ה-right הוא רוחב האייקון חלקי 2 (24px) + הריפוד (16px) = 40px */}
          <div className="absolute top-0 bottom-0 w-0.5 z-0" style={{ right: '55px', backgroundColor: COLORS.border }} />

          {processedItems.map((item, index) => {
            const isCurrentTime = currentItemMatch?.index === item.index;
            const isNowIndicatorHere = index === nowItemIndex && !currentItemMatch;
            const isPast = item.realTime < currentTotalMinutes;
            const prevItemRealTime = index === 0 ? item.realTime : processedItems[index - 1].realTime;
            let diffBefore = 0, diffAfter = 0, diff = 0;

            if (isNowIndicatorHere) {
              diffBefore = Math.max(0, currentTotalMinutes - prevItemRealTime);
              diffAfter = Math.max(0, item.realTime - currentTotalMinutes);
            } else {
              diff = Math.max(0, item.realTime - prevItemRealTime);
              if (index === 0) diff = 0;
            }

            return (
              <div key={item.index} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {isNowIndicatorHere && (
                  <>
                    <FlexibleSpacer minutes={diffBefore} />
                    <NowIndicator currentTimeStr={currentTimeStr} />
                    <FlexibleSpacer minutes={diffAfter} />
                  </>
                )}
                {!isNowIndicatorHere && <FlexibleSpacer minutes={diff} />}

                {item.type === 'annotation'
                  ? <AnnotationItem item={item} isPast={isPast} />
                  : item.type === 'simple'
                    ? <SimpleItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />
                    : <HighlightedItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />
                }
              </div>
            );
          })}

          {nowItemIndex === processedItems.length && !currentItemMatch && (
            <>
              <div style={{ flexGrow: 1, minHeight: '20px' }} />
              <NowIndicator currentTimeStr={currentTimeStr} />
            </>
          )}
          <div style={{ minHeight: '5px', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default Normal;